import React, { useEffect, useRef, useState } from 'react';
import {
  Loader2,
  Filter,
  Upload,
  Send,
  Download,
  ChevronRight,
} from 'lucide-react';
import {
  adminListOrders,
  adminUpdateOrderStatus,
  adminAddInternalNote,
  adminUploadDeliverable,
  getOrderItems,
  getOrderFiles,
  getOrderEvents,
  getOrderFileUrl,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  formatCashPrice,
  type CommerceOrder,
  type CommerceOrderItem,
  type CommerceOrderFile,
  type CommerceOrderEvent,
} from '@rehub/commerce';
import { supabase } from '../services/supabase';

interface CommerceAdminTabProps {
  adminUserId: string;
}

const STATUSES = [
  'all',
  'awaiting_files',
  'submitted',
  'in_progress',
  'review',
  'completed',
  'cancelled',
] as const;

export default function CommerceAdminTab({ adminUserId }: CommerceAdminTabProps) {
  const [orders, setOrders] = useState<CommerceOrder[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [items, setItems] = useState<CommerceOrderItem[]>([]);
  const [files, setFiles] = useState<CommerceOrderFile[]>([]);
  const [events, setEvents] = useState<CommerceOrderEvent[]>([]);
  const [internalNote, setInternalNote] = useState('');
  const [notifyLoading, setNotifyLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadOrders = async () => {
    setLoading(true);
    const data = await adminListOrders(supabase, statusFilter);
    setOrders(data);
    setLoading(false);
  };

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  const selectOrder = async (id: string) => {
    setSelectedId(id);
    const [i, f, e] = await Promise.all([
      getOrderItems(supabase, id),
      getOrderFiles(supabase, id),
      getOrderEvents(supabase, id),
    ]);
    setItems(i);
    setFiles(f);
    setEvents(e);
  };

  const selected = orders.find((o) => o.id === selectedId);

  const updateStatus = async (status: string) => {
    if (!selectedId) return;
    await adminUpdateOrderStatus(supabase, selectedId, status, adminUserId);
    await loadOrders();
    await selectOrder(selectedId);
    if (status === 'completed' || status === 'review') {
      await supabase.functions.invoke('commerce-notify', {
        body: { orderId: selectedId, event: status === 'completed' ? 'delivered' : 'status_update', message: `Status: ${status}` },
      });
    }
  };

  const handleDeliverable = async (fileList: FileList | null) => {
    if (!selectedId || !fileList?.length) return;
    for (const file of Array.from(fileList)) {
      await adminUploadDeliverable(supabase, { orderId: selectedId, adminUserId, file });
    }
    await selectOrder(selectedId);
  };

  const handleInternalNote = async () => {
    if (!selectedId || !internalNote.trim()) return;
    await adminAddInternalNote(supabase, selectedId, adminUserId, internalNote.trim());
    setInternalNote('');
    await selectOrder(selectedId);
  };

  const notifyCustomer = async () => {
    if (!selectedId) return;
    setNotifyLoading(true);
    await supabase.functions.invoke('commerce-notify', {
      body: { orderId: selectedId, event: 'status_update', message: `Your order status is: ${selected?.status}` },
    });
    setNotifyLoading(false);
  };

  const downloadFile = async (path: string, name: string) => {
    const url = await getOrderFileUrl(supabase, path);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-full min-h-0">
      <div className="lg:w-96 shrink-0 flex flex-col min-h-0">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All statuses' : ORDER_STATUS_LABELS[s as keyof typeof ORDER_STATUS_LABELS]}
              </option>
            ))}
          </select>
          <button type="button" onClick={loadOrders} className="text-xs text-violet-400 hover:text-violet-300">
            Refresh
          </button>
        </div>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-violet-400" />
          </div>
        ) : (
          <ul className="overflow-y-auto space-y-2 flex-1 max-h-[60vh]">
            {orders.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => selectOrder(o.id)}
                  className={`w-full text-left rounded-lg border p-3 text-sm ${
                    selectedId === o.id ? 'border-violet-500 bg-violet-950/30' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <div className="flex justify-between items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">{o.id.slice(0, 8)}</span>
                    <ChevronRight size={14} className="text-slate-500" />
                  </div>
                  <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${ORDER_STATUS_COLORS[o.status]}`}>
                    {ORDER_STATUS_LABELS[o.status]}
                  </span>
                  <p className="text-xs text-slate-500 mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex-1 min-w-0 overflow-y-auto">
        {!selected ? (
          <p className="text-slate-500 text-sm py-8 text-center">Select an order to manage</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center justify-between">
              <h3 className="font-semibold text-white">Order {selected.id.slice(0, 8)}</h3>
              <span className={`text-xs px-2 py-1 rounded-full ${ORDER_STATUS_COLORS[selected.status]}`}>
                {ORDER_STATUS_LABELS[selected.status]}
              </span>
            </div>

            <div className="text-sm text-slate-400 space-y-1">
              <p>Total: {formatCashPrice(selected.cash_amount_pence, selected.currency)}</p>
              {selected.guest_email && <p>Guest: {selected.guest_email}</p>}
              {selected.customer_notes && <p>Brief: {selected.customer_notes}</p>}
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-2">Items</p>
              <ul className="text-sm space-y-1">
                {items.map((i) => (
                  <li key={i.id}>{i.title}</li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap gap-2">
              {(['submitted', 'in_progress', 'review', 'completed', 'cancelled'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => updateStatus(s)}
                  className="text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-200"
                >
                  → {ORDER_STATUS_LABELS[s]}
                </button>
              ))}
            </div>

            <div>
              <p className="text-xs font-medium text-slate-400 mb-2">Customer uploads</p>
              <ul className="space-y-1 text-sm">
                {files.filter((f) => f.kind === 'upload').map((f) => (
                  <li key={f.id} className="flex justify-between">
                    <span>{f.file_name}</span>
                    <button type="button" onClick={() => downloadFile(f.storage_path, f.file_name)} className="text-violet-400">
                      <Download size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-xs font-medium text-slate-400 mb-2">Deliverables</p>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => handleDeliverable(e.target.files)} />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-2 text-sm text-violet-400 hover:text-violet-300 mb-2"
              >
                <Upload size={14} /> Upload completed files
              </button>
              <ul className="space-y-1 text-sm">
                {files.filter((f) => f.kind === 'deliverable').map((f) => (
                  <li key={f.id}>{f.file_name}</li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-xs font-medium text-slate-400 mb-2">Internal note</p>
              <div className="flex gap-2">
                <input
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm"
                />
                <button type="button" onClick={handleInternalNote} className="px-3 py-1.5 bg-slate-700 rounded text-sm">
                  Add
                </button>
              </div>
            </div>

            <button
              type="button"
              disabled={notifyLoading}
              onClick={notifyCustomer}
              className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm text-white hover:bg-violet-500 disabled:opacity-60"
            >
              {notifyLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Notify customer
            </button>

            <div>
              <p className="text-xs font-medium text-slate-400 mb-2">Timeline</p>
              <ul className="text-xs space-y-2 max-h-40 overflow-y-auto">
                {events.map((ev) => (
                  <li key={ev.id} className={`border-l-2 pl-2 ${ev.is_internal ? 'border-amber-700 text-amber-200/80' : 'border-slate-600 text-slate-400'}`}>
                    {ev.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
